import React, { useEffect, useState } from 'react';
import { getApi } from '../utils/api';

interface TaskStatusTrackerProps {
  taskId: string;
  onComplete?: (result: any) => void;
  pollingInterval?: number; // ms
  showDetails?: boolean;
}

const TaskStatusTracker: React.FC<TaskStatusTrackerProps> = ({
  taskId,
  onComplete,
  pollingInterval = 2000,
  showDetails = false,
}) => {
  const [status, setStatus] = useState<'pending' | 'in_progress' | 'completed' | 'failed'>('pending');
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(true);

  const api = getApi();

  useEffect(() => {
    let intervalId: number | null = null;
    
    const checkStatus = async () => {
      try {
        const response = await api.get(`/v1/import/task/${taskId}`);
        const data = response.data;
        
        setStatus(data.status);
        
        if (data.status === 'completed') {
          setResult(data.result);
          if (onComplete) onComplete(data.result);
          setIsPolling(false);
        } else if (data.status === 'failed') {
          setError(data.error || 'Task failed');
          setIsPolling(false);
        }
      } catch (err) {
        console.error('Error checking task status:', err);
      }
    };
    
    if (isPolling && taskId) {
      // Check immediately
      checkStatus();
      // Then start polling
      intervalId = window.setInterval(checkStatus, pollingInterval);
    }
    
    return () => {
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, [taskId, isPolling, pollingInterval, onComplete]);

  const resumePolling = () => setIsPolling(true);
  
  // Status indicators
  const renderStatus = () => {
    switch (status) {
      case 'pending':
        return (
          <div className="flex items-center space-x-2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            <span>Task pending...</span>
          </div>
        );
      case 'in_progress':
        return (
          <div className="flex items-center space-x-2">
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            <span>Processing...</span>
          </div>
        );
      case 'completed':
        return (
          <div className="text-green-600 flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Task completed!</span>
          </div>
        );
      case 'failed':
        return (
          <div className="text-red-600 flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Task failed</span>
            <button 
              className="ml-2 px-2 py-1 text-xs rounded bg-red-100 text-red-800 hover:bg-red-200"
              onClick={resumePolling}
            >
              Retry
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 p-3 rounded-md border">
      {renderStatus()}
      
      {error && (
        <div className="mt-2 p-2 bg-red-100 text-red-800 rounded">
          <span>Error: {error}</span>
        </div>
      )}
      
      {showDetails && result && status === 'completed' && (
        <div className="mt-2">
          <h4 className="text-sm font-medium">Result:</h4>
          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto mt-1">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default TaskStatusTracker;